import {useTranslation} from 'react-i18next';
import {Info} from 'lucide-react';
import {NumberedList, cn} from '@chekinapp/ui';
import {usePendingPaymentSteps} from './usePendingPaymentSteps';

function WelcomeSteps({totalGuests}: {totalGuests: number}) {
  const {t} = useTranslation();
  const payments = usePendingPaymentSteps();

  if (payments.length === 0) return null;

  const steps = [
    {
      title: t('welcome_step_register_guests', {count: totalGuests}),
      hint: t('welcome_step_register_guests_hint'),
    },
    ...payments.map(step => ({title: t(step.title), hint: t(step.hint)})),
  ];

  return (
    <NumberedList
      className="mb-5 rounded-[16px] bg-[var(--chekin-color-surface-pressed)] p-4 [--numbered-list-marker-bg:var(--chekin-blue-tint-info)] [--numbered-list-marker-text:var(--chekin-color-brand-blue)]"
      itemClassName="gap-3"
    >
      {steps.map(step => (
        <span key={step.title} className="block min-w-0">
          <b className="block text-[14px] font-semibold leading-5 text-[var(--chekin-color-brand-navy)]">
            {step.title}
          </b>
          <span className="block text-[13px] leading-[18px] text-[var(--chekin-text-secondary)]">
            {step.hint}
          </span>
        </span>
      ))}
    </NumberedList>
  );
}

function WelcomeGuestCountNote({
  bookedGuests,
  totalGuests,
}: {
  bookedGuests?: number;
  totalGuests: number;
}) {
  const {t} = useTranslation();
  const isMismatch = typeof bookedGuests === 'number' && totalGuests !== bookedGuests;

  return (
    <p
      className={cn(
        'mt-4 flex gap-2 text-[14px] leading-[21px]',
        isMismatch
          ? 'text-[var(--chekin-red-soft)]'
          : 'text-[var(--chekin-text-secondary)]',
      )}
    >
      <Info
        size={16}
        className={cn(
          'mt-0.5 shrink-0',
          !isMismatch && 'text-[var(--chekin-text-tertiary)]',
        )}
        aria-hidden
      />
      <span aria-live="polite">
        {isMismatch
          ? t('welcome_guest_count_mismatch', {count: bookedGuests})
          : t('welcome_prefilled_note')}
      </span>
    </p>
  );
}

export {WelcomeGuestCountNote, WelcomeSteps};
